import React, { useMemo, useState } from 'react';
import { Modal, Button, Table, Tag, Space, message } from 'antd';
import {
  PrinterOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  TrophyOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import {
  calculateCourseAssessmentBreakdown,
  getGradeDetails,
} from '../../../../Utils/gradeUtils';
import { generateTranscriptPdf } from '../../../../Utils/TranscriptPdfGenerator';
import './style.scss';

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

const AcademicTranscriptModal = ({
  isOpen,
  onClose,
  courseGroup,
  userProfile,
}) => {
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  const breakdown = useMemo(() => {
    if (!courseGroup || !courseGroup.assessments) return null;
    return calculateCourseAssessmentBreakdown(courseGroup.assessments);
  }, [courseGroup]);

  const isStudent = userProfile?.role === 'student';
  const caEligibility = breakdown?.caEligibility;

  // CA Table Columns (Marks shown for CA assignments)
  const caColumns = useMemo(() => {
    return [
      {
        title: '#',
        dataIndex: 'index',
        key: 'index',
        width: '6%',
        render: (_, __, idx) => idx + 1,
      },
      {
        title: 'Continuous Assessment',
        dataIndex: 'name',
        key: 'name',
        width: '36%',
        render: (text) => <span style={{ fontWeight: 600 }}>{text}</span>,
      },
      {
        title: 'Due Date',
        dataIndex: 'endDate',
        key: 'endDate',
        width: '18%',
        render: (d) => formatDate(d),
      },
      {
        title: 'Status',
        dataIndex: 'attempt',
        key: 'status',
        width: '14%',
        render: (attempt) => {
          if (!attempt || !attempt.id) {
            return <Tag color="error">Not Attempted</Tag>;
          }
          if (attempt.marks !== null && attempt.marks !== undefined && attempt.marks !== '') {
            return <Tag color="success">Graded</Tag>;
          }
          return <Tag color="warning">Submitted</Tag>;
        },
      },
      {
        title: 'Marks',
        dataIndex: 'attempt',
        key: 'marks',
        width: '12%',
        align: 'right',
        render: (attempt) => {
          const m = attempt?.marks;
          if (m !== null && m !== undefined && m !== '' && !isNaN(Number(m))) {
            return <strong>{Number(m)}%</strong>;
          }
          return <span style={{ color: '#94a3b8' }}>-</span>;
        },
      },
      {
        title: 'Grade',
        dataIndex: 'attempt',
        key: 'grade',
        width: '14%',
        render: (attempt) => {
          const m = attempt?.marks;
          if (m !== null && m !== undefined && m !== '' && !isNaN(Number(m))) {
            const details = getGradeDetails(m);
            return (
              <Tag color={details.tagColor} style={{ fontWeight: 700 }}>
                Grade {details.grade} • {details.classification}
              </Tag>
            );
          }
          return <span style={{ color: '#94a3b8' }}>Pending</span>;
        },
      },
    ];
  }, []);

  // Final Exam Columns (strictly NO numeric marks for students)
  const finalExamColumns = useMemo(() => {
    const cols = [
      {
        title: '#',
        dataIndex: 'index',
        key: 'index',
        width: '6%',
        render: (_, __, idx) => idx + 1,
      },
      {
        title: 'Final Examination Component',
        dataIndex: 'name',
        key: 'name',
        width: isStudent ? '42%' : '36%',
        render: (text) => <span style={{ fontWeight: 600 }}>{text}</span>,
      },
      {
        title: 'Due Date',
        dataIndex: 'endDate',
        key: 'endDate',
        width: '18%',
        render: (d) => formatDate(d),
      },
      {
        title: 'Status',
        dataIndex: 'attempt',
        key: 'status',
        width: '16%',
        render: (attempt) => {
          if (caEligibility?.status === 'NOT_ELIGIBLE') {
            return <Tag color="error">Locked (Ineligible)</Tag>;
          }
          if (!attempt || !attempt.id) {
            return <Tag color="default">Not Attempted</Tag>;
          }
          if (attempt.marks !== null && attempt.marks !== undefined && attempt.marks !== '') {
            return <Tag color="success">Graded</Tag>;
          }
          return <Tag color="warning">Submitted</Tag>;
        },
      },
    ];

    if (!isStudent) {
      cols.push({
        title: 'Marks',
        dataIndex: 'attempt',
        key: 'marks',
        width: '12%',
        align: 'right',
        render: (attempt) => {
          if (caEligibility?.status === 'NOT_ELIGIBLE') {
            return <span style={{ color: '#ef4444' }}>0%</span>;
          }
          const m = attempt?.marks;
          if (m !== null && m !== undefined && m !== '' && !isNaN(Number(m))) {
            return <strong>{Number(m)}%</strong>;
          }
          return <span style={{ color: '#94a3b8' }}>-</span>;
        },
      });
    }

    cols.push({
      title: 'Grade',
      dataIndex: 'attempt',
      key: 'grade',
      width: isStudent ? '18%' : '14%',
      render: (attempt) => {
        if (caEligibility?.status === 'NOT_ELIGIBLE') {
          return <Tag color="error">Grade F • Fail</Tag>;
        }
        const m = attempt?.marks;
        if (m !== null && m !== undefined && m !== '' && !isNaN(Number(m))) {
          const details = getGradeDetails(m);
          return (
            <Tag color={details.tagColor} style={{ fontWeight: 700 }}>
              Grade {details.grade} • {details.classification}
            </Tag>
          );
        }
        return <span style={{ color: '#94a3b8' }}>Pending</span>;
      },
    });

    return cols;
  }, [isStudent, caEligibility]);

  if (!courseGroup || !breakdown) return null;

  const handlePrint = () => {
    setTimeout(() => {
      window.print();
    }, 150);
  };

  const handleDownloadPdf = async () => {
    setIsDownloadingPdf(true);
    try {
      await generateTranscriptPdf({
        courseGroup,
        breakdown,
        userProfile,
      });
      message.success('Official Academic Transcript PDF downloaded successfully!');
    } catch (error) {
      console.error('Failed to generate transcript PDF:', error);
      message.error(error?.message ? `Failed to download transcript PDF: ${error.message}` : 'Failed to download transcript PDF. Please try again.');
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const studentName = userProfile?.name || 'Student';
  const studentIndex = userProfile?.indexNumber || `STU-${userProfile?.id || '0000'}`;
  const studentEmail = userProfile?.email || 'N/A';
  const issuedDate = new Date().toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const {
    caList,
    finalExamList,
    caPerformance,
    finalPerformance,
    overallMark,
    overallGrade,
    overallClassification,
    isFullyCompleted,
  } = breakdown;

  const getOutcomeTag = () => {
    if (caEligibility.status === 'NOT_ELIGIBLE') {
      return { text: 'FAILED (INELIGIBLE)', className: 'failed' };
    }
    if (isFullyCompleted) {
      if (overallMark >= 50) {
        return { text: 'PASSED', className: 'passed' };
      }
      return { text: 'FAILED', className: 'failed' };
    }
    return { text: 'IN PROGRESS', className: 'in-progress' };
  };

  const outcome = getOutcomeTag();

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      width={typeof window !== 'undefined' && window.innerWidth < 768 ? '95%' : 900}
      footer={[
        <Button key="close" onClick={onClose} className="no-print">
          Close
        </Button>,
        <Button
          key="print"
          icon={<PrinterOutlined />}
          onClick={handlePrint}
          className="no-print"
        >
          Print
        </Button>,
        <Button
          key="download"
          type="primary"
          icon={<DownloadOutlined />}
          loading={isDownloadingPdf}
          onClick={handleDownloadPdf}
          className="no-print"
          style={{
            background: '#0284c7',
            borderColor: '#0284c7',
            color: '#ffffff',
            fontWeight: 600,
            borderRadius: 6,
            boxShadow: '0 2px 4px rgba(2, 132, 199, 0.25)',
          }}
        >
          Download Transcript (PDF)
        </Button>,
      ]}
      className="academic-transcript-modal"
    >
      <div className="transcript-document">
        {/* Document Header */}
        <div className="transcript-header">
          <div className="institution-info">
            <div className="inst-badge">Official Academic Record</div>
            <h1>INSTITUTE OF BUSINESS AND ACCOUNTING STUDIES</h1>
            <div className="inst-sub">IBAS Academic Transcript of Continuous & Final Assessment</div>
          </div>
          <div className="transcript-meta">
            <div className="doc-title">Course Transcript</div>
            <div>Date Issued: {issuedDate}</div>
            <div>Status: <strong>Verified</strong></div>
          </div>
        </div>

        {/* Student and Course Information Grid */}
        <div className="student-course-grid">
          <div className="info-column">
            <div className="info-row">
              <span className="label">Student Name:</span>
              <span className="value">{studentName}</span>
            </div>
            <div className="info-row">
              <span className="label">Student Index / ID:</span>
              <span className="value">{studentIndex}</span>
            </div>
            <div className="info-row">
              <span className="label">Student Email:</span>
              <span className="value">{studentEmail}</span>
            </div>
          </div>
          <div className="info-column">
            <div className="info-row">
              <span className="label">Course Title:</span>
              <span className="value">{courseGroup?.courseName}</span>
            </div>
            <div className="info-row">
              <span className="label">Course Code:</span>
              <span className="value">{courseGroup?.courseCode || 'N/A'}</span>
            </div>
            <div className="info-row">
              <span className="label">Grading System:</span>
              <span className="value">Standard (Distinction Pass ≥85%, Merit Pass ≥75%, Credit Pass ≥65%, Ordinary Pass ≥50%)</span>
            </div>
          </div>
        </div>

        {/* PART 1: CONTINUOUS ASSESSMENTS (CA) */}
        <div className="transcript-section">
          <div className="section-title">
            <h3>
              <SafetyCertificateOutlined style={{ color: '#0284c7' }} />
              PART 1: CONTINUOUS ASSESSMENTS (CA) BREAKDOWN
            </h3>
            <span style={{ fontSize: 12, color: '#64748b' }}>
              Minimum Requirement for Final Exam: Average ≥ 50.0%
            </span>
          </div>

          <Table
            columns={caColumns}
            dataSource={caList}
            rowKey="id"
            pagination={false}
            size="small"
            bordered
            locale={{ emptyText: 'No Continuous Assessments assigned.' }}
          />

          {/* CA Eligibility Status Banner */}
          <div
            className={`eligibility-banner ${
              caEligibility.status === 'ELIGIBLE'
                ? 'eligible'
                : caEligibility.status === 'NOT_ELIGIBLE'
                ? 'ineligible'
                : 'pending'
            }`}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {caEligibility.status === 'ELIGIBLE' && (
                <CheckCircleOutlined style={{ fontSize: 20, color: '#16a34a' }} />
              )}
              {caEligibility.status === 'NOT_ELIGIBLE' && (
                <CloseCircleOutlined style={{ fontSize: 20, color: '#dc2626' }} />
              )}
              {caEligibility.status === 'PENDING' && (
                <ClockCircleOutlined style={{ fontSize: 20, color: '#d97706' }} />
              )}
              <div>
                <div className="banner-text">
                  Continuous Assessment Average: <strong>{caEligibility.caAverage !== null ? `${caEligibility.caAverage}%` : 'N/A'}</strong> (Required: ≥ 50%)
                </div>
                <div style={{ fontSize: 12, opacity: 0.9 }}>{caEligibility.reason}</div>
              </div>
            </div>

            <Tag
              color={caEligibility.tagColor}
              style={{ fontSize: 12, padding: '4px 10px', fontWeight: 700 }}
            >
              {caEligibility.label}
            </Tag>
          </div>
        </div>

        {/* PART 2: FINAL EXAMINATION */}
        <div className="transcript-section">
          <div className="section-title">
            <h3>
              <TrophyOutlined style={{ color: '#d97706' }} />
              PART 2: FINAL EXAMINATION
            </h3>
            <span style={{ fontSize: 12, color: '#64748b' }}>
              Weightage: 50% of Final Composite Grade
            </span>
          </div>

          <Table
            columns={finalExamColumns}
            dataSource={finalExamList}
            rowKey="id"
            pagination={false}
            size="small"
            bordered
            locale={{ emptyText: 'No Final Examination assigned.' }}
          />
        </div>

        {/* PART 3: FINAL COURSE COMPOSITE EVALUATION */}
        <div className="transcript-section">
          <div className="section-title">
            <h3>
              <TrophyOutlined style={{ color: '#7c3aed' }} />
              PART 3: OVERALL COMPOSITE EVALUATION
            </h3>
            <span style={{ fontSize: 12, color: '#64748b' }}>
              {isStudent
                ? 'Evaluation: Continuous Assessment (50%) + Final Examination (50%)'
                : 'Formula: (CA Average × 0.50) + (Final Exam × 0.50)'}
            </span>
          </div>

          <div className="composite-summary-grid">
            <div className="summary-card">
              <div className="card-label">CA Average (50%)</div>
              <div className="card-val" style={{ color: '#0284c7' }}>
                {caEligibility.caAverage !== null ? `${caEligibility.caAverage}%` : 'Pending'}
              </div>
              <div className="card-sub">
                {caEligibility.gradedCAs}/{caEligibility.totalCAs} CAs Evaluated
              </div>
            </div>

            <div className="summary-card">
              <div className="card-label">Final Exam (50%)</div>
              <div className="card-val" style={{ color: '#d97706' }}>
                {isStudent ? (
                  caEligibility.status === 'NOT_ELIGIBLE'
                    ? 'Grade F'
                    : finalPerformance.averageMarks !== null
                    ? `Grade ${getGradeDetails(finalPerformance.averageMarks).grade}`
                    : 'Pending'
                ) : (
                  caEligibility.status === 'NOT_ELIGIBLE'
                    ? 'Ineligible (0%)'
                    : finalPerformance.averageMarks !== null
                    ? `${finalPerformance.averageMarks}%`
                    : 'Pending'
                )}
              </div>
              <div className="card-sub">
                {isStudent && finalPerformance.averageMarks !== null
                  ? getGradeDetails(finalPerformance.averageMarks).classification
                  : caEligibility.status === 'NOT_ELIGIBLE'
                  ? 'Fail (Ineligible)'
                  : finalExamList.length > 0
                  ? (finalPerformance.isGraded ? 'Exam Evaluated' : 'Awaiting Exam')
                  : 'No Exam'}
              </div>
            </div>

            <div className="summary-card" style={{ background: '#f0fdf4', borderColor: '#bbf7d0' }}>
              <div className="card-label" style={{ color: '#166534' }}>
                {isStudent ? 'Final Standing' : 'Composite Mark (100%)'}
              </div>
              <div className="card-val" style={{ color: '#15803d' }}>
                {isStudent
                  ? (overallGrade ? `Grade ${overallGrade}` : 'Pending')
                  : (overallMark !== null ? `${overallMark}%` : 'Pending')}
              </div>
              <div className="card-sub">
                {isStudent ? overallClassification : 'Final Course Score'}
              </div>
            </div>
          </div>

          {/* Official Award Result Banner */}
          <div className="award-result-banner">
            <div className="award-left">
              <div className="award-title">Final Academic Award & Standing</div>
              <div className="award-grade">
                Grade {overallGrade}
              </div>
              <div className="award-classification">
                Classification: <strong>{overallClassification}</strong>
              </div>
            </div>

            <div className="award-right">
              <div className={`outcome-badge ${outcome.className}`}>
                {outcome.text}
              </div>
              <div className="criteria-text">
                Evaluated in accordance with IBAS Academic Standards
              </div>
            </div>
          </div>
        </div>

        {/* Footer & Certification */}
        <div className="transcript-footer">
          <div className="system-generated-notice">
            <div className="notice-title">This is an official system generated transcript and does not require any signature.</div>
            <div className="notice-sub">Grading Scale: Distinction Pass (≥ 85%), Merit Pass (≥ 75%), Credit Pass (≥ 65%), Ordinary Pass (≥ 50%), Not Qualified (&lt; 50%) • Issued under the authority of IBAS LMS</div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AcademicTranscriptModal;
